import { Dayjs } from 'dayjs';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';

import { formatOffset } from 'utils/date';

import { InputAdornment, TextField } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useState } from 'react';

type TimePickerProps = {
  control: Control<any>;
  name: string;
  label: string;
  variant?: 'standard' | 'outlined' | 'filled';
  disabled?: boolean;
  onChange?: (date: Dayjs | null) => void;
  fullWidth?: boolean;
  baseDate?: Dayjs | null | undefined;
};

export default function ControlledTimePicker({
  control,
  name,
  label,
  variant = 'outlined',
  disabled,
  onChange,
  fullWidth,
  baseDate
}: TimePickerProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => (
          <>
            <TimePicker
              {...field}
              value={field.value || baseDate}
              label={label}
              ampm={false}
              disabled={disabled}
              renderInput={(props) => (
                <TextField
                  {...props}
                  sx={{
                    width: fullWidth ? '100%' : '140px'
                  }}
                  variant={variant}
                  error={!!fieldState?.error?.type}
                  helperText={fieldState?.error?.message}
                  required
                  disabled={disabled}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <>
                        {field.value?.isValid() && (
                          <InputAdornment position="end">
                            {formatOffset(
                              field.value.toDate(),
                              baseDate?.toDate()
                            )}
                          </InputAdornment>
                        )}
                        {props.InputProps?.endAdornment}
                      </>
                    )
                  }}
                />
              )}
              onAccept={(date) => {
                onChange?.(date);
              }}
              onOpen={() => {
                setIsOpen(true);
              }}
              onClose={() => {
                setIsOpen(false);
              }}
              onChange={(date) => {
                field.onChange(date);
                if (onChange && !isOpen) {
                  onChange(date);
                }
              }}
            />
          </>
        )}
      />
    </LocalizationProvider>
  );
}
